import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useToast } from '../../components/UI/ToastProvider';
import { Button } from '../../components/UI/Button';
import { ConfirmModal } from '../../components/UI/ConfirmModal';

import { workspaceService, WorkspaceDto, DocumentScopeMode } from '../../services/workspaceService';
import { workspaceDocumentService, WorkspaceDocumentDto } from '../../services/workspaceDocumentService';
import { documentService, DocumentPickerNode } from '../../services/documentService';
import { documentNodePermissionsService } from '../../services/documentNodePermissionsService';
import {
  workspaceAccessService,
  WorkspaceAccessRuleDto,
  WorkspaceAccessSourceType,
  WorkspaceAccessActionType,
} from '../../services/workspaceAccessService';
import {
  workspaceChatHistoryService,
  WorkspaceConversationSummary,
  WorkspaceChatMessage,
} from '../../services/workspaceChatHistoryService';
import { readImageFileAsDataUrl, validateWorkspaceAvatarFile } from '../../utils/avatarUtils';
import { useWorkspaceAvatarSrc } from '../../components/UI/WorkspaceAvatar';
import { agentBotService } from '../../services/agentBotService';
import { AgentBotType } from '../../types/atendimentoEnums';
import { chatModelSupportsVision } from '../../utils/chatModelVision';
import {
  extractPromptUrls,
  normalizePromptUrl,
  promptLinkLabelForUrl,
  prunePromptLinkLabels,
  upsertPromptLinkLabel,
} from '../../utils/workspacePromptLinks';

function apiErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const ax = error as { response?: { data?: unknown } };
    const d = ax.response?.data;
    if (typeof d === 'string' && d.trim()) return d;
    if (d && typeof d === 'object') {
      const o = d as Record<string, unknown>;
      if (typeof o.message === 'string') return o.message;
      if (typeof o.title === 'string') return o.title;
    }
  }
  return fallback;
}



// Componente de Aba
const TabItem: React.FC<{
  title: string;
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
}> = ({ title, icon, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
      isActive
        ? 'bg-blue-600 text-white shadow-md'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
    }`}
  >
    {icon}
    <span className="font-medium">{title}</span>
  </button>
);

type WorkspaceTabProps = {
  workspace: WorkspaceDto;
  onPatch: (patch: Partial<WorkspaceDto>) => void;
  onSave: () => Promise<boolean>;
  persistedChatModel?: string;
};

// Aba Geral
const GeneralTab: React.FC<WorkspaceTabProps> = ({ workspace, onPatch, onSave }) => {
  const [avatarError, setAvatarError] = useState<string | undefined>();
  const promptUrls = useMemo(
    () => extractPromptUrls(workspace.systemPrompt || ''),
    [workspace.systemPrompt]
  );

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file)
    {
      return;
    }

    const validationError = validateWorkspaceAvatarFile(file);
    if (validationError)
    {
      setAvatarError(validationError);
      return;
    }

    try
    {
      const dataUrl = await readImageFileAsDataUrl(file);
      setAvatarError(undefined);
      onPatch({ avatar: dataUrl });
    }
    catch
    {
      setAvatarError('Não foi possível processar a imagem selecionada.');
    }
  };

  const removeImage = () => {
    setAvatarError(undefined);
    onPatch({ avatar: undefined });
  };

  const avatarSrc = useWorkspaceAvatarSrc(workspace.id, workspace.avatar);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações Básicas</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome do Chatbot *
            </label>
            <input
              type="text"
              value={workspace.name}
              onChange={(e) => onPatch({ name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome do Assistente
            </label>
            <input
              type="text"
              value={workspace.assistantName || ''}
              onChange={(e) => onPatch({ assistantName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nome que aparecerá para os usuários"
            />
            <p className="text-xs text-gray-500 mt-1">Nome que será exibido para os usuários finais</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={workspace.isActive ? 'active' : 'inactive'}
              onChange={(e) => onPatch({ isActive: e.target.value === 'active' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
              <option value="maintenance">Manutenção</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Imagem do Assistente
            </label>
            <div className="space-y-3">
              {avatarSrc ? (
                <div className="flex items-center space-x-3">
                  <img
                    src={avatarSrc}
                    alt="Avatar do assistente"
                    className="h-16 w-16 rounded-full object-cover border-2 border-gray-200"
                  />
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => document.getElementById('avatar-upload')?.click()}
                      variant="secondary"
                      size="sm"
                    >
                      Alterar
                    </Button>
                    <Button
                      onClick={removeImage}
                      variant="danger"
                      size="sm"
                    >
                      Remover
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
                    <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <Button
                    onClick={() => document.getElementById('avatar-upload')?.click()}
                    variant="secondary"
                    size="sm"
                  >
                    + Adicionar Imagem
                  </Button>
                </div>
              )}
              <input
                id="avatar-upload"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageUpload}
                className="hidden"
              />
              {avatarError ? (
                <p className="text-xs text-red-600">{avatarError}</p>
              ) : (
                <p className="text-xs text-gray-500">
                  Recomendado: imagem quadrada de 256x256 pixels. JPG, PNG ou WebP até 5 MB.
                </p>
              )}
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descrição
            </label>
            <textarea
              value={workspace.description || ''}
              onChange={(e) => onPatch({ description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Persona
            </label>
            <textarea
              value={workspace.persona || ''}
              onChange={(e) => onPatch({ persona: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Tom, papel e estilo do agente"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Idioma padrão
            </label>
            <input
              type="text"
              value={workspace.defaultLanguage || ''}
              onChange={(e) => onPatch({ defaultLanguage: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="ex.: pt-BR, en"
            />
          </div>

          <div className="flex items-center">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={workspace.isPublic === true}
                onChange={(e) => onPatch({ isPublic: e.target.checked })}
                className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Workspace público (visitante)</span>
            </label>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prompt do Sistema
            </label>
            <textarea
              value={workspace.systemPrompt || ''}
              onChange={(e) => {
                const systemPrompt = e.target.value;
                onPatch({
                  systemPrompt,
                  promptLinkLabels: prunePromptLinkLabels(
                    workspace.promptLinkLabels,
                    systemPrompt
                  ),
                });
              }}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Instruções para o comportamento do chatbot..."
            />
            <p className="text-xs text-gray-500 mt-1">Define como o chatbot deve se comportar e responder</p>
          </div>

          {promptUrls.length > 0 && (
            <div className="md:col-span-2 space-y-3 rounded-md border border-gray-200 p-3">
              <div>
                <h3 className="text-sm font-medium text-gray-900">Botões dos links do prompt</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Nomeie cada URL encontrada no Prompt do Sistema. Sem nome, o widget exibe o link; com nome, exibe botão.
                </p>
              </div>
              {promptUrls.map((url) => (
                <div key={normalizePromptUrl(url)} className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600 break-all">
                    {url}
                  </label>
                  <input
                    type="text"
                    value={promptLinkLabelForUrl(workspace.promptLinkLabels, url)}
                    onChange={(e) =>
                      onPatch({
                        promptLinkLabels: upsertPromptLinkLabel(
                          workspace.promptLinkLabels,
                          url,
                          e.target.value,
                          workspace.systemPrompt || ''
                        ),
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Nome do botão"
                  />
                </div>
              ))}
            </div>
          )}

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mensagem de Recusa
            </label>
            <textarea
              value={workspace.rejectionMessage || ''}
              onChange={(e) => onPatch({ rejectionMessage: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Mensagem exibida quando o chatbot não pode ajudar..."
            />
            <p className="text-xs text-gray-500 mt-1">Mensagem exibida quando o chatbot não pode atender à solicitação</p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mensagens Rápidas (uma por linha)
            </label>
            <textarea
              value={workspace.quickMessages.join('\n')}
              onChange={(e) =>
                onPatch({
                  quickMessages: e.target.value
                    .split('\n')
                    .map((msg: string) => msg.trim())
                    .filter(Boolean),
                })
              }
              rows={4}
              placeholder="Digite as mensagens rápidas, uma por linha..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="mt-6">
          <Button onClick={() => void onSave()}>
            Salvar Configurações Básicas
          </Button>
        </div>
      </div>
    </div>
  );
};

// Aba Chat (LLM)
const ChatTab: React.FC<WorkspaceTabProps> = ({ workspace, onPatch, onSave, persistedChatModel }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [checkingBots, setCheckingBots] = useState(false);
  const modelSupportsVision = chatModelSupportsVision(workspace.chatModel);

  const requestSave = async () => {
    const currentModel = workspace.chatModel || '';
    const savedModel = persistedChatModel || '';
    const modelChanged = currentModel !== savedModel;
    if (modelChanged && !modelSupportsVision && workspace.id)
    {
      setCheckingBots(true);
      try
      {
        const bots = await agentBotService.getAll();
        const linkedToAttendance = bots.some((bot) =>
          bot.workspaceId === workspace.id
          && bot.botType === AgentBotType.KnowCopilot);
        if (linkedToAttendance)
        {
          setConfirmOpen(true);
          return;
        }
      }
      catch
      {
        // A lista de robôs é só o aviso. O servidor continua sem enviar imagem a modelo sem visão.
      }
      finally
      {
        setCheckingBots(false);
      }
    }

    await onSave();
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações de IA</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Provedor de LLM
            </label>
            <select
              value={workspace.chatProvider || 'openai'}
              onChange={(e) => onPatch({ chatProvider: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="openai">OpenAI</option>
              <option value="anthropic">Anthropic</option>
              <option value="gemini">Google Gemini</option>
              <option value="ollama">Ollama</option>
              <option value="local">Local</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Modelo
            </label>
            <select
              value={workspace.chatModel || 'gpt-4o-mini'}
              onChange={(e) => onPatch({ chatModel: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="gpt-4o-mini">GPT-4o Mini (Recomendado)</option>
              <option value="gpt-4o">GPT-4o</option>
              <option value="gpt-4">GPT-4</option>
              <option value="gpt-3.5-turbo-1106">GPT-3.5 Turbo</option>
              <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Legado)</option>
              <option value="claude-3-sonnet">Claude 3 Sonnet</option>
              <option value="claude-3-haiku">Claude 3 Haiku</option>
              <option value="gemini-pro">Gemini Pro</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {modelSupportsVision
                ? 'Este modelo pode descrever imagens no chat e no atendimento, quando a interpretação de imagens estiver ligada.'
                : 'Este modelo não interpreta imagens. Fotos e anexos continuam salvos para o operador; o robô responde só ao texto.'}
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={workspace.visionEnabled === true}
                onChange={(e) => onPatch({ visionEnabled: e.target.checked })}
              />
              <span>
                Interpretar imagens enviadas no chat e no atendimento.
                <span className="block text-xs text-gray-500">
                  Só tem efeito com modelo que aceita visão. Com a opção desligada, o arquivo continua gravado.
                </span>
              </span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              API Key
            </label>
            <input
              type="password"
              value={workspace.apiKey || ''}
              onChange={(e) => onPatch({ apiKey: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={workspace.hasApiKey ? '•••••••• (configurada — deixe em branco para manter)' : 'sk-...'}
              autoComplete="new-password"
            />
            {workspace.hasApiKey && !workspace.apiKey ? (
              <p className="mt-1 text-xs text-gray-500">
                Chave já configurada. Informe um valor novo apenas para rotacionar.
              </p>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Modo do Chat
            </label>
            <select
              value={workspace.chatMode || 'chat'}
              onChange={(e) => onPatch({ chatMode: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="chat">Chat (Conversação)</option>
              <option value="query">Consulta (Resposta Direta)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Temperatura
            </label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={workspace.aiTemperature ?? 0.7}
              onChange={(e) => onPatch({ aiTemperature: parseFloat(e.target.value) })}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Determinístico (0)</span>
              <span>{workspace.aiTemperature ?? 0.7}</span>
              <span>Criativo (2)</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tamanho do Histórico
            </label>
            <input
              type="number"
              value={workspace.aiHistory}
              onChange={(e) => onPatch({ aiHistory: parseInt(e.target.value, 10) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="1"
              max="100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tamanho Máximo da Resposta
            </label>
            <input
              type="number"
              value={workspace.maxResponseTokens}
              onChange={(e) => onPatch({ maxResponseTokens: parseInt(e.target.value, 10) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="500"
              max="8000"
            />
            <p className="text-xs text-gray-500 mt-1">Número máximo de tokens na resposta do assistente (500-8000)</p>
          </div>
        </div>

        <div className="mt-6">
          <Button onClick={() => void requestSave()} disabled={checkingBots}>
            Salvar Configurações de IA
          </Button>
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmOpen}
        title="Modelo sem interpretação de imagem"
        message="Este workspace está ligado a um robô de atendimento. Anexos de WhatsApp e dos outros canais continuam gravados e visíveis para o operador. O robô deixa de interpretar imagens e responde apenas ao texto."
        confirmText="Salvar mesmo assim"
        cancelText="Manter o modelo anterior"
        onCancel={() => {
          setConfirmOpen(false);
          if (persistedChatModel)
          {
            onPatch({ chatModel: persistedChatModel });
          }
        }}
        onConfirm={() => {
          setConfirmOpen(false);
          void onSave();
        }}
      />
    </div>
  );
};

// Aba Banco Vetorial
const VectorTab: React.FC<WorkspaceTabProps> = ({ workspace, onPatch, onSave }) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Banco de Dados Vetorial</h3>
        <p className="text-gray-600 mb-6">
          Credenciais e configurações do seu banco de dados vetorial. Essas chaves devem estar corretas para o funcionamento adequado.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Provedor do Banco
            </label>
            <select
              value={workspace.vectorDbProvider || 'pgvector'}
              onChange={(e) => onPatch({ vectorDbProvider: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="pgvector">PGVector (PostgreSQL)</option>
              <option value="lancedb">LanceDB</option>
              <option value="chroma">Chroma</option>
              <option value="pinecone">Pinecone</option>
              <option value="weaviate">Weaviate</option>
              <option value="qdrant">Qdrant</option>
              <option value="milvus">Milvus</option>
              <option value="zilliz">Zilliz Cloud</option>
              <option value="astra">AstraDB</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferência de Acurácia
            </label>
            <select
              value={workspace.accuracyPreference || 'balanced'}
              onChange={(e) => onPatch({ accuracyPreference: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="speed">Velocidade</option>
              <option value="balanced">Equilibrado</option>
              <option value="accuracy">Acurácia</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Top N (Resultados)
            </label>
            <input
              type="number"
              value={workspace.topN}
              onChange={(e) => onPatch({ topN: parseInt(e.target.value, 10) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="1"
              max="20"
            />
            <p className="text-xs text-gray-500 mt-1">Número de resultados mais relevantes a considerar</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Janela de Contexto Máxima
            </label>
            <input
              type="number"
              value={workspace.maxContextWindow}
              onChange={(e) => onPatch({ maxContextWindow: parseInt(e.target.value, 10) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="1000"
              max="32000"
            />
            <p className="text-xs text-gray-500 mt-1">Tamanho máximo da janela de contexto</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quantidade Máxima de Trechos
            </label>
            <input
              type="number"
              value={workspace.maxChunks}
              onChange={(e) => onPatch({ maxChunks: parseInt(e.target.value, 10) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="100"
              max="10000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Limiar de Similaridade
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={workspace.similarityThreshold ?? 0.25}
              onChange={(e) => onPatch({ similarityThreshold: parseFloat(e.target.value) })}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Baixo (0)</span>
              <span>{workspace.similarityThreshold ?? 0.25}</span>
              <span>Alto (1)</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tamanho dos Trechos
            </label>
            <input
              type="number"
              value={workspace.chunkSize}
              onChange={(e) => onPatch({ chunkSize: parseInt(e.target.value, 10) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="100"
              max="4000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sobreposição de Trechos
            </label>
            <input
              type="number"
              value={workspace.chunkOverlap}
              onChange={(e) => onPatch({ chunkOverlap: parseInt(e.target.value, 10) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="0"
              max="1000"
            />
          </div>
        </div>

        <div className="mt-6">
          <Button onClick={() => void onSave()}>
            Salvar Configurações de Banco Vetorial
          </Button>
        </div>
      </div>
    </div>
  );
};

const TypeIcon: React.FC<{ type: string }> = ({ type }) => {
  const base = 'h-9 w-9 rounded-full flex items-center justify-center shrink-0';
  switch (type) {
    case 'role':
      return (
        <div className={`${base} bg-blue-100 text-blue-700`} title="Papel">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
        </div>
      );
    case 'team':
      return (
        <div className={`${base} bg-purple-100 text-purple-700`} title="Equipe">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
      );
    case 'user':
    default:
      return (
        <div className={`${base} bg-green-100 text-green-700`} title="Usuário">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
      );
  }
};

const typeLabel = (t: string) =>
  t === 'role' ? 'Papel' : t === 'team' ? 'Equipe' : t === 'user' ? 'Usuário' : t;

// Aba Membros — ACL do agente (usuário / papel / equipe + permitir / negar)
const MembersTab: React.FC<WorkspaceTabProps> = ({ workspace }) => {
  const workspaceId = workspace.id;
  const { addToast } = useToast();

  const [rules, setRules] = useState<WorkspaceAccessRuleDto[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [roleNameById, setRoleNameById] = useState<Map<string, string>>(() => new Map());
  const [teamNameById, setTeamNameById] = useState<Map<string, string>>(() => new Map());
  const [userNameById, setUserNameById] = useState<Map<string, string>>(() => new Map());
  const [userList, setUserList] = useState<{ id: string; name: string; email?: string }[]>([]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [addSourceType, setAddSourceType] = useState<WorkspaceAccessSourceType>('role');
  const [addSourceId, setAddSourceId] = useState('');
  const [addAction, setAddAction] = useState<WorkspaceAccessActionType>('allow');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [savingRule, setSavingRule] = useState(false);

  const [confirmRemove, setConfirmRemove] = useState<{ open: boolean; row: WorkspaceAccessRuleDto | null }>({
    open: false,
    row: null,
  });

  const ruleKey = (r: WorkspaceAccessRuleDto) =>
    `${String(r.sourceType).toLowerCase()}:${String(r.sourceId).toLowerCase()}`;

  const existingKeys = useMemo(() => new Set(rules.map(ruleKey)), [rules]);

  const loadRules = useCallback(async () => {
    if (!workspaceId) return;
    setRulesLoading(true);
    try {
      const list = await workspaceAccessService.listByWorkspace(workspaceId);
      setRules(list);
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Erro ao carregar regras de acesso do agente.'), 'error');
      setRules([]);
    } finally {
      setRulesLoading(false);
    }
  }, [workspaceId, addToast]);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const [roles, teams, users] = await Promise.all([
        documentNodePermissionsService.getAvailableRoles(),
        documentNodePermissionsService.getAvailableTeams(),
        documentNodePermissionsService.getAvailableUsers(),
      ]);
      const rMap = new Map<string, string>();
      roles.forEach(r => rMap.set(String(r.id).toLowerCase(), r.name));
      setRoleNameById(rMap);
      const tMap = new Map<string, string>();
      teams.forEach(t => tMap.set(String(t.id).toLowerCase(), t.name));
      setTeamNameById(tMap);
      const uMap = new Map<string, string>();
      const ul: { id: string; name: string; email?: string }[] = [];
      users.forEach(u => {
        const id = String(u.id);
        const label = u.email ? `${u.name} (${u.email})` : u.name;
        uMap.set(id.toLowerCase(), label);
        ul.push({ id, name: u.name, email: u.email });
      });
      setUserNameById(uMap);
      setUserList(ul);
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Erro ao carregar papéis, equipes e usuários.'), 'error');
    } finally {
      setCatalogLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    if (!workspaceId) return;
    void loadRules();
    void loadCatalog();
  }, [workspaceId, loadRules, loadCatalog]);

  useEffect(() => {
    if (!showAddModal) {
      setAddSourceId('');
      setUserSearchTerm('');
      setShowUserDropdown(false);
      setAddAction('allow');
    }
  }, [showAddModal]);

  useEffect(() => {
    setAddSourceId('');
    setUserSearchTerm('');
    setShowUserDropdown(false);
  }, [addSourceType]);

  useEffect(() => {
    if (addSourceType !== 'user' || !showAddModal || !addSourceId) return;
    const u = userList.find(x => x.id === addSourceId);
    const expected = u ? (u.email ? `${u.name} (${u.email})` : u.name) : '';
    if (userSearchTerm.trim() !== expected.trim()) setAddSourceId('');
  }, [userSearchTerm, addSourceId, addSourceType, userList, showAddModal]);

  const resolveEntityName = useCallback(
    (row: WorkspaceAccessRuleDto): string => {
      const id = String(row.sourceId).toLowerCase();
      const t = String(row.sourceType).toLowerCase();
      if (t === 'role') return roleNameById.get(id) ?? `Papel (${row.sourceId})`;
      if (t === 'team') return teamNameById.get(id) ?? `Equipe (${row.sourceId})`;
      if (t === 'user') return userNameById.get(id) ?? `Usuário (${row.sourceId})`;
      return row.sourceId;
    },
    [roleNameById, teamNameById, userNameById]
  );

  const filteredRoles = useMemo(() => {
    const entries = Array.from(roleNameById.entries());
    return entries
      .filter(([id]) => !existingKeys.has(`role:${id}`))
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }, [roleNameById, existingKeys]);

  const filteredTeams = useMemo(() => {
    const entries = Array.from(teamNameById.entries());
    return entries
      .filter(([id]) => !existingKeys.has(`team:${id}`))
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }, [teamNameById, existingKeys]);

  const userSearchResults = useMemo(() => {
    const q = userSearchTerm.trim().toLowerCase();
    if (q.length < 2) return [];
    return userList
      .filter(
        u =>
          !existingKeys.has(`user:${u.id.toLowerCase()}`) &&
          (u.name.toLowerCase().includes(q) || (u.email ?? '').toLowerCase().includes(q))
      )
      .slice(0, 50);
  }, [userList, userSearchTerm, existingKeys]);

  const saveNewRule = async () => {
    if (!workspaceId) return;
    if (!addSourceId) {
      addToast('Selecione ou busque uma entidade.', 'warning');
      return;
    }
    setSavingRule(true);
    try {
      await workspaceAccessService.addRule(workspaceId, {
        sourceType: addSourceType,
        sourceId: addSourceId,
        action: addAction,
      });
      addToast('Regra adicionada.', 'success');
      setShowAddModal(false);
      await loadRules();
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Não foi possível salvar a regra.'), 'error');
    } finally {
      setSavingRule(false);
    }
  };

  const removeRule = async () => {
    const row = confirmRemove.row;
    if (!row || !workspaceId) return;
    try {
      await workspaceAccessService.removeRule(workspaceId, row.id);
      setRules(prev => prev.filter(x => x.id !== row.id));
      addToast('Regra removida.', 'success');
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Erro ao remover regra.'), 'error');
    } finally {
      setConfirmRemove({ open: false, row: null });
    }
  };

  const addModalBusy = catalogLoading || savingRule;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Membros e acesso ao agente</h3>
        <p className="text-sm text-gray-600 mt-1 max-w-3xl">
          Sem regras configuradas: o agente fica disponível para todos os usuários do tenant. Ao adicionar regras, apenas
          usuários/papéis/equipes com permissão &quot;Permitir&quot; terão acesso. &quot;Negar&quot; prevalece sobre
          &quot;Permitir&quot;.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <h4 className="text-base font-medium text-gray-800">Regras atuais</h4>
        <Button type="button" variant="primary" onClick={() => setShowAddModal(true)} disabled={!workspaceId}>
          Adicionar regra
        </Button>
      </div>

      {rulesLoading ? (
        <p className="text-gray-500">Carregando regras...</p>
      ) : rules.length === 0 ? (
        <p className="text-gray-500">Nenhuma regra de acesso. O agente está disponível para todo o tenant.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-700 w-24">Tipo</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Nome da entidade</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Ação</th>
                <th className="text-right py-3 px-4 font-medium text-gray-700">Ações</th>
              </tr>
            </thead>
            <tbody>
              {rules.map(row => {
                const isDeny = String(row.action).toLowerCase() === 'deny';
                return (
                  <tr key={row.id} className="border-t border-gray-100">
                    <td className="py-3 px-4 align-middle">
                      <div className="flex items-center gap-2">
                        <TypeIcon type={String(row.sourceType).toLowerCase()} />
                        <span className="text-gray-600">{typeLabel(String(row.sourceType).toLowerCase())}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 align-middle font-medium text-gray-900">{resolveEntityName(row)}</td>
                    <td className="py-3 px-4 align-middle">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          isDeny ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {isDeny ? 'Negar' : 'Permitir'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right align-middle">
                      <Button
                        variant="danger"
                        size="sm"
                        type="button"
                        onClick={() => setConfirmRemove({ open: true, row })}
                        title="Remover regra"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4"
          role="presentation"
          onClick={() => !savingRule && setShowAddModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6"
            role="dialog"
            aria-modal="true"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-2">Adicionar regra de acesso</h3>
            <p className="text-sm text-gray-600 mb-4">
              Escolha o tipo, a entidade e se o acesso é permitido ou negado para este agente.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <select
                  value={addSourceType}
                  onChange={e => setAddSourceType(e.target.value as WorkspaceAccessSourceType)}
                  disabled={addModalBusy}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="user">Usuário</option>
                  <option value="role">Papel</option>
                  <option value="team">Equipe</option>
                </select>
              </div>

              {addSourceType === 'user' ? (
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Buscar usuário</label>
                  <input
                    type="search"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Nome ou e-mail (mín. 2 caracteres)"
                    value={userSearchTerm}
                    disabled={addModalBusy}
                    onChange={e => {
                      setUserSearchTerm(e.target.value);
                      setShowUserDropdown(true);
                    }}
                    onFocus={() => setShowUserDropdown(true)}
                    autoFocus
                  />
                  {showUserDropdown && userSearchTerm.trim().length >= 2 && userSearchResults.length > 0 && (
                    <ul className="absolute z-10 mt-1 w-full max-h-48 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
                      {userSearchResults.map(u => (
                        <li key={u.id}>
                          <button
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                            onClick={() => {
                              setAddSourceId(u.id);
                              setUserSearchTerm(u.email ? `${u.name} (${u.email})` : u.name);
                              setShowUserDropdown(false);
                            }}
                          >
                            <span className="font-medium text-gray-900">{u.name}</span>
                            {u.email ? <span className="text-gray-500 text-xs block">{u.email}</span> : null}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {showUserDropdown && userSearchTerm.trim().length >= 2 && userSearchResults.length === 0 && !catalogLoading && (
                    <p className="text-xs text-gray-500 mt-1">Nenhum usuário encontrado ou todos já possuem regra.</p>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {addSourceType === 'role' ? 'Papel' : 'Equipe'}
                  </label>
                  <select
                    value={addSourceId}
                    onChange={e => setAddSourceId(e.target.value)}
                    disabled={addModalBusy}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">
                      {addSourceType === 'role' ? 'Selecione um papel' : 'Selecione uma equipe'}
                    </option>
                    {(addSourceType === 'role' ? filteredRoles : filteredTeams).map(opt => (
                      <option key={opt.id} value={opt.id}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                  {catalogLoading && <p className="text-xs text-gray-500 mt-1">Carregando lista...</p>}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ação</label>
                <select
                  value={addAction}
                  onChange={e => setAddAction(e.target.value as WorkspaceAccessActionType)}
                  disabled={addModalBusy}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="allow">Permitir</option>
                  <option value="deny">Negar</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2 flex-wrap">
              <Button type="button" variant="secondary" onClick={() => setShowAddModal(false)} disabled={savingRule}>
                Cancelar
              </Button>
              <Button type="button" variant="primary" onClick={() => void saveNewRule()} disabled={addModalBusy}>
                {savingRule ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmRemove.open}
        title="Remover regra"
        message={
          confirmRemove.row
            ? `Remover a regra para ${resolveEntityName(confirmRemove.row)} (${typeLabel(String(confirmRemove.row.sourceType))})?`
            : ''
        }
        onConfirm={() => void removeRule()}
        onCancel={() => setConfirmRemove({ open: false, row: null })}
        confirmText="Remover"
        cancelText="Cancelar"
      />
    </div>
  );
};

// Aba Configurações Avançadas (não usada nas abas atuais; mantida para reuso)
const AdvancedTab: React.FC<WorkspaceTabProps> = ({ workspace, onPatch, onSave }) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações Avançadas</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Top N (Resultados)
            </label>
            <input
              type="number"
              value={workspace.topN}
              onChange={(e) => onPatch({ topN: parseInt(e.target.value, 10) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="1"
              max="20"
            />
            <p className="text-xs text-gray-500 mt-1">Número de resultados mais relevantes a considerar</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Janela de Contexto Máxima
            </label>
            <input
              type="number"
              value={workspace.maxContextWindow}
              onChange={(e) => onPatch({ maxContextWindow: parseInt(e.target.value, 10) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="1000"
              max="32000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <input
              type="text"
              value={(workspace.tags ?? []).join(', ')}
              onChange={(e) =>
                onPatch({ tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag) })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="tag1, tag2, tag3"
            />
            <p className="text-xs text-gray-500 mt-1">Tags separadas por vírgula para categorização</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Configurações Específicas do Provedor
            </label>
            <textarea
              value={Object.entries(workspace.providerSpecificSettings ?? {}).map(([key, value]) => `${key}: ${value}`).join('\n')}
              onChange={(e) => {
                const lines = e.target.value.split('\n');
                const settings: Record<string, string> = {};
                lines.forEach(line => {
                  const [key, value] = line.split(':').map(s => s.trim());
                  if (key && value) {
                    settings[key] = value;
                  }
                });
                onPatch({ providerSpecificSettings: settings });
              }}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="chave1: valor1&#10;chave2: valor2"
            />
            <p className="text-xs text-gray-500 mt-1">Configurações específicas do provedor (chave: valor)</p>
          </div>
        </div>

        <div className="mt-6">
          <Button onClick={() => void onSave()}>
            Salvar Configurações Avançadas
          </Button>
        </div>
      </div>
    </div>
  );
};

// Aba Provedor de Embedding
const EmbeddingTab: React.FC<WorkspaceTabProps> = ({ workspace, onPatch, onSave }) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Provedor de Embedding</h3>
        <p className="text-gray-600 mb-6">
          Credenciais e configurações do seu provedor de embedding. Essas chaves devem estar corretas para o funcionamento adequado.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Provedor de Embedding
            </label>
            <select
              value={workspace.embeddingProvider || 'openai'}
              onChange={(e) => onPatch({ embeddingProvider: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="openai">OpenAI</option>
              <option value="anthropic">Anthropic</option>
              <option value="gemini">Google Gemini</option>
              <option value="cohere">Cohere</option>
              <option value="voyageai">VoyageAI</option>
              <option value="native">Native</option>
              <option value="ollama">Ollama</option>
              <option value="localai">LocalAI</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Modelo de Embedding
            </label>
            <select
              value={workspace.embeddingModel || 'text-embedding-3-small'}
              onChange={(e) => onPatch({ embeddingModel: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="text-embedding-3-small">text-embedding-3-small (Recomendado)</option>
              <option value="text-embedding-3-large">text-embedding-3-large</option>
              <option value="text-embedding-ada-002">text-embedding-ada-002 (Legado)</option>
              <option value="text-embedding-002">text-embedding-002</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Modelo específico para geração de embeddings</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quantidade máxima de trechos
            </label>
            <input
              type="number"
              value={workspace.maxChunks}
              onChange={(e) => onPatch({ maxChunks: parseInt(e.target.value, 10) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="100"
              max="10000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tamanho dos trechos
            </label>
            <input
              type="number"
              value={workspace.chunkSize}
              onChange={(e) => onPatch({ chunkSize: parseInt(e.target.value, 10) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="100"
              max="4000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sobreposição de trechos
            </label>
            <input
              type="number"
              value={workspace.chunkOverlap}
              onChange={(e) => onPatch({ chunkOverlap: parseInt(e.target.value, 10) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="0"
              max="1000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Modo de Busca Vetorial
            </label>
            <select
              value={workspace.vectorSearchMode || 'similarity'}
              onChange={(e) => onPatch({ vectorSearchMode: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="similarity">Similaridade</option>
              <option value="hybrid">Híbrido</option>
              <option value="rerank">Re-ranqueamento</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Estratégia de busca vetorial</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Acesso
            </label>
            <select
              value={workspace.accessType || 'DocumentTreeBased'}
              onChange={(e) => onPatch({ accessType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="DocumentTreeBased">Baseado na Árvore de Documentos</option>
              <option value="Global">Global</option>
              <option value="Restricted">Restrito</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Tipo de acesso aos documentos</p>
          </div>
        </div>

        <div className="mt-6">
          <Button onClick={() => void onSave()}>
            Salvar Configurações de Embedding
          </Button>
        </div>
      </div>
    </div>
  );
};

const isFolderNodeType = (nodeType?: string): boolean =>
  (nodeType || '').toLowerCase() === 'folder';

/** Pasta já vinculada cujo path cobre docPath (filho estrito, alinhado ao prefixo no backend). */
const findCoveringFolderBinding = (
  scopeRows: WorkspaceDocumentDto[],
  docPath: string
): WorkspaceDocumentDto | null => {
  const normalized = docPath.replace(/\/$/, '');
  if (!normalized) return null;
  for (const row of scopeRows) {
    if (!isFolderNodeType(row.documentNodeType) || !row.documentPath) continue;
    const fp = row.documentPath.replace(/\/$/, '');
    if (!fp) continue;
    const prefix = fp.endsWith('/') ? fp : `${fp}/`;
    if (normalized !== fp && normalized.startsWith(prefix)) return row;
  }
  return null;
};

const SCOPE_MODE_OPTIONS: { value: DocumentScopeMode; label: string; description: string }[] = [
  {
    value: 'bypass',
    label: 'Repositório completo (padrão)',
    description:
      'Todos os documentos indexados neste workspace (não recomendado para bots de atendimento externo). A permissão do usuário na árvore continua valendo no Copilot autenticado.',
  },
  {
    value: 'allowlist',
    label: 'Lista de permissão (allowlist)',
    description:
      'APENAS os documentos/pastas listados abaixo. Se a lista estiver vazia, o workspace usa o repositório completo (compatibilidade). Pastas sem documentos-folha não abrem o repositório — o contexto fica vazio.',
  },
  {
    value: 'blocklist',
    label: 'Lista de bloqueio (blocklist)',
    description:
      'Todos os documentos indexados EXCETO os listados abaixo. Ao bloquear uma pasta, tudo abaixo dela deixa de entrar no contexto. Lista vazia equivale ao repositório completo no Copilot; bots de atendimento externo não respondem até haver exclusões.',
  },
];

const DocumentScopeTab: React.FC<{
  workspace: WorkspaceDto;
  onPatch: (patch: Partial<WorkspaceDto>) => void;
  onSave: () => Promise<boolean>;
}> = ({ workspace, onPatch, onSave }) => {
  const workspaceId = workspace.id;
  const { addToast } = useToast();
  const scopeMode: DocumentScopeMode = (workspace.documentScopeMode as DocumentScopeMode) || 'bypass';
  const [scopeModeSaving, setScopeModeSaving] = useState(false);
  const [scopeDocs, setScopeDocs] = useState<WorkspaceDocumentDto[]>([]);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [addPickerKind, setAddPickerKind] = useState<'folder' | 'document' | null>(null);
  const [searchQ, setSearchQ] = useState('');
  const [pickerNodes, setPickerNodes] = useState<DocumentPickerNode[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [addingDocId, setAddingDocId] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<{ open: boolean; row: WorkspaceDocumentDto | null }>({
    open: false,
    row: null,
  });

  const isBlocklist = scopeMode === 'blocklist';
  const showDocList = scopeMode !== 'bypass';

  const sortedScopeDocs = useMemo(() => {
    return [...scopeDocs].sort((a, b) => {
      const fa = isFolderNodeType(a.documentNodeType) ? 0 : 1;
      const fb = isFolderNodeType(b.documentNodeType) ? 0 : 1;
      if (fa !== fb) return fa - fb;
      return (a.documentPath || a.documentName || '').localeCompare(b.documentPath || b.documentName || '');
    });
  }, [scopeDocs]);

  const loadScope = useCallback(async () => {
    if (!workspaceId) return;
    setScopeLoading(true);
    try {
      const list = await workspaceDocumentService.getByWorkspaceId(workspaceId);
      setScopeDocs(list);
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Erro ao carregar documentos do escopo.'), 'error');
    } finally {
      setScopeLoading(false);
    }
  }, [workspaceId, addToast]);

  useEffect(() => {
    void loadScope();
  }, [loadScope]);

  useEffect(() => {
    if (!showAddDoc || !addPickerKind) return;
    let cancelled = false;
    setPickerLoading(true);
    const load =
      addPickerKind === 'folder'
        ? documentService.getFolderPickerNodes('/', 8, workspaceId)
        : documentService.getDocumentsForPicker('/', 8, workspaceId);
    void load
      .then(nodes => {
        if (!cancelled) setPickerNodes(nodes);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          addToast(apiErrorMessage(e, 'Erro ao carregar itens para seleção.'), 'error');
          setPickerNodes([]);
        }
      })
      .finally(() => {
        if (!cancelled) setPickerLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showAddDoc, addPickerKind, addToast, workspaceId]);

  useEffect(() => {
    if (!showAddDoc) {
      setSearchQ('');
      setAddPickerKind(null);
    }
  }, [showAddDoc]);

  const filteredPicker = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (q.length < 2) return [];
    return pickerNodes
      .filter(
        n =>
          n.title.toLowerCase().includes(q) ||
          n.fullPath.toLowerCase().includes(q) ||
          n.path.toLowerCase().includes(q)
      )
      .slice(0, 150);
  }, [pickerNodes, searchQ]);

  const linkedIds = useMemo(() => new Set(scopeDocs.map(d => d.documentId)), [scopeDocs]);

  const handleModeChange = async (mode: DocumentScopeMode) => {
    if (mode === scopeMode)
    {
      return;
    }

    const previousMode = scopeMode;
    onPatch({ documentScopeMode: mode });
    setScopeModeSaving(true);
    try
    {
      const saved = await onSave();
      if (!saved)
      {
        onPatch({ documentScopeMode: previousMode });
      }
    }
    finally
    {
      setScopeModeSaving(false);
    }
  };

  const addDocument = async (node: DocumentPickerNode) => {
    const documentId = node.id;
    if (!workspaceId) return;
    if (linkedIds.has(documentId)) {
      addToast('Este item já está na lista.', 'info');
      return;
    }
    if (!isFolderNodeType(node.nodeType)) {
      const path = node.fullPath || node.path;
      const covering = findCoveringFolderBinding(scopeDocs, path);
      if (covering) {
        addToast(
          `Este documento já fica coberto pela pasta "${covering.documentName || covering.documentPath}". Vinculá-lo de novo é redundante.`,
          'info'
        );
      }
    }
    setAddingDocId(documentId);
    try {
      await workspaceDocumentService.create({ workspaceId, documentId, isActive: true, priority: 0 });
      const isF = isFolderNodeType(node.nodeType);
      addToast(
        isBlocklist
          ? isF
            ? 'Pasta bloqueada para este agente.'
            : 'Documento bloqueado para este agente.'
          : isF
            ? 'Pasta associada ao agente.'
            : 'Documento associado ao agente.',
        'success'
      );
      await loadScope();
      setShowAddDoc(false);
      setSearchQ('');
    } catch (err: unknown) {
      addToast(apiErrorMessage(err, 'Erro ao vincular item ao escopo.'), 'error');
    } finally {
      setAddingDocId(null);
    }
  };

  const removeDocument = async () => {
    const row = confirmRemove.row;
    if (!row) return;
    try {
      await workspaceDocumentService.delete(row.id);
      setScopeDocs(prev => prev.filter(x => x.id !== row.id));
      const wasFolder = isFolderNodeType(row.documentNodeType);
      addToast(
        isBlocklist
          ? wasFolder
            ? 'Pasta desbloqueada.'
            : 'Documento desbloqueado.'
          : wasFolder
            ? 'Pasta removida do escopo.'
            : 'Documento removido do escopo.',
        'success'
      );
    } catch (err: unknown) {
      addToast(apiErrorMessage(err, 'Erro ao remover documento.'), 'error');
    } finally {
      setConfirmRemove({ open: false, row: null });
    }
  };

  const pickerBtnLabel = (already: boolean, loading: boolean) => {
    if (already) return 'Já na lista';
    if (loading) return '...';
    return isBlocklist ? 'Bloquear' : 'Adicionar';
  };

  const tableLabel = isBlocklist ? 'Itens bloqueados (pastas ou documentos)' : 'Itens no escopo (pastas ou documentos)';
  const emptyLabel = isBlocklist
    ? 'Nenhum bloqueio configurado. No Copilot autenticado o agente usa o repositório completo (compatibilidade). Bots de atendimento externo não respondem com lista vazia — adicione pelo menos um item.'
    : 'Nenhum item selecionado. Enquanto a allowlist estiver vazia, o workspace usa todos os documentos já indexados (modo de compatibilidade). Inclua pastas ou documentos para restringir o escopo.';

  const modalTitle =
    addPickerKind === 'folder'
      ? isBlocklist
        ? 'Bloquear pasta'
        : 'Incluir pasta'
      : isBlocklist
        ? 'Bloquear documento'
        : 'Incluir documento';

  const confirmRow = confirmRemove.row;
  const confirmIsFolder = confirmRow ? isFolderNodeType(confirmRow.documentNodeType) : false;
  const confirmTitle = confirmIsFolder
    ? isBlocklist
      ? 'Desbloquear pasta'
      : 'Remover pasta do escopo'
    : isBlocklist
      ? 'Desbloquear documento'
      : 'Remover documento do escopo';
  const confirmMessage = confirmIsFolder
    ? isBlocklist
      ? 'Os documentos desta pasta voltarão a poder ser usados como contexto (salvo outros bloqueios).'
      : 'O escopo deixará de incluir esta pasta e o conteúdo abaixo dela (salvo outros vínculos na lista).'
    : isBlocklist
      ? 'O documento voltará a ser considerado pelo agente como fonte de contexto.'
      : 'O agente deixará de usar este documento como fonte explícita no escopo.';
  const confirmBtn = isBlocklist ? 'Desbloquear' : 'Remover';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Escopo documental</h3>
        <p className="text-sm text-gray-600 mt-1 max-w-3xl">
          Defina o escopo por <strong className="font-medium text-gray-800">pastas</strong> (toda a subárvore) ou por{' '}
          <strong className="font-medium text-gray-800">documentos</strong> avulsos. A permissão do usuário na árvore de documentos
          continua sendo aplicada.
        </p>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <strong className="font-medium">Atenção:</strong> documentos sem permissões explícitas na árvore serão acessíveis a todos
        os usuários com acesso a este workspace. Configure as permissões de visualização antes de indexar conteúdo sensível.
      </div>

      {/* --- Mode selector --- */}
      <fieldset className="space-y-3" disabled={scopeModeSaving}>
        <legend className="sr-only">Modo de escopo documental</legend>
        {SCOPE_MODE_OPTIONS.map(opt => (
          <label
            key={opt.value}
            className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
              scopeMode === opt.value
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="documentScopeMode"
              value={opt.value}
              checked={scopeMode === opt.value}
              onChange={() => void handleModeChange(opt.value)}
              className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <div>
              <span className="font-medium text-gray-900">{opt.label}</span>
              <p className="text-sm text-gray-600 mt-0.5">{opt.description}</p>
            </div>
          </label>
        ))}
      </fieldset>

      {scopeModeSaving && (
        <p className="text-sm text-blue-600">Salvando modo de escopo...</p>
      )}

      {/* --- Document list (allowlist / blocklist) --- */}
      {showDocList && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
            <h4 className="text-base font-medium text-gray-800">{tableLabel}</h4>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="primary"
                onClick={() => {
                  setAddPickerKind('folder');
                  setShowAddDoc(true);
                }}
              >
                {isBlocklist ? 'Bloquear pasta' : 'Incluir pasta'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setAddPickerKind('document');
                  setShowAddDoc(true);
                }}
              >
                {isBlocklist ? 'Bloquear documento' : 'Incluir documento'}
              </Button>
            </div>
          </div>

          {scopeLoading ? (
            <p className="text-gray-500">Carregando vínculos...</p>
          ) : scopeDocs.length === 0 ? (
            <>
              <p className="text-gray-500">{emptyLabel}</p>
              {scopeMode === 'allowlist' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <strong className="font-medium">Lista vazia.</strong> Sua allowlist ainda não tem documentos ou pastas
                  configurados. Enquanto estiver vazia, o workspace usará <strong>todos os documentos já indexados</strong>{' '}
                  (modo de compatibilidade). Adicione pelo menos um item para restringir o acesso. Bots de atendimento
                  externo não respondem neste estado.
                </div>
              )}
              {scopeMode === 'blocklist' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <strong className="font-medium">Lista vazia.</strong> Sem exclusões, o Copilot autenticado usa o
                  repositório completo. <strong>Bots de atendimento externo não respondem</strong> até você bloquear pelo
                  menos um documento ou pasta.
                </div>
              )}
            </>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Tipo</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Nome</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Caminho</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Ativo</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedScopeDocs.map(row => {
                    const folderRow = isFolderNodeType(row.documentNodeType);
                    return (
                      <tr key={row.id} className="border-t border-gray-100">
                        <td className="py-3 px-4 text-gray-700">
                          <span
                            className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${
                              folderRow ? 'bg-amber-100 text-amber-900' : 'bg-slate-100 text-slate-800'
                            }`}
                          >
                            {folderRow ? 'Pasta' : 'Documento'}
                          </span>
                        </td>
                        <td className="py-3 px-4">{row.documentName || row.documentId}</td>
                        <td className="py-3 px-4 text-gray-600">{row.documentPath || '—'}</td>
                        <td className="py-3 px-4">{row.isActive ? 'Sim' : 'Não'}</td>
                        <td className="py-3 px-4 text-right">
                          <Button
                            variant="danger"
                            size="sm"
                            type="button"
                            onClick={() => setConfirmRemove({ open: true, row })}
                            title={isBlocklist ? 'Desbloquear' : 'Remover do escopo'}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* --- Bypass info box --- */}
      {scopeMode === 'bypass' && (
        <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          Nenhuma restrição documental configurada. O Copilot autenticado poderá usar qualquer documento indexado do tenant ao
          qual o usuário tenha acesso na árvore. <strong>Não use este modo em bots de atendimento externo</strong> — configure
          uma allowlist ou blocklist.
        </div>
      )}

      {/* --- Picker modal --- */}
      {showAddDoc && addPickerKind && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">{modalTitle}</h3>
            <p className="text-sm text-gray-600 mb-3">
              {addPickerKind === 'folder'
                ? 'A pasta inteira entra na regra: em modo restritivo, só o que estiver sob ela; em modo de bloqueio, tudo abaixo dela fica bloqueado (sem exceção por documento filho).'
                : 'Apenas este documento entra na lista (não inclui irmãos na mesma pasta).'}
            </p>
            <p className="text-sm text-gray-600 mb-3">
              Filtre por título ou caminho (mínimo 2 caracteres).
            </p>
            <input
              type="search"
              className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Buscar por título ou caminho..."
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              autoFocus
            />
            {pickerLoading && <p className="text-sm text-gray-500">Carregando...</p>}
            {!pickerLoading && searchQ.trim().length < 2 && (
              <p className="text-sm text-gray-500">Digite ao menos 2 caracteres para filtrar a lista.</p>
            )}
            {!pickerLoading && searchQ.trim().length >= 2 && filteredPicker.length === 0 && (
              <p className="text-sm text-gray-500">Nenhum resultado.</p>
            )}
            <ul className="divide-y border border-gray-100 rounded-md max-h-64 overflow-y-auto mb-4 mt-2">
              {filteredPicker.map(n => {
                const already = linkedIds.has(n.id);
                return (
                  <li key={n.id} className="py-2 px-3 flex justify-between items-center gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate">{n.title}</div>
                      <div className="text-xs text-gray-500 truncate">{n.fullPath || n.path}</div>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={already || addingDocId === n.id}
                      onClick={() => void addDocument(n)}
                    >
                      {pickerBtnLabel(already, addingDocId === n.id)}
                    </Button>
                  </li>
                );
              })}
            </ul>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowAddDoc(false);
                  setSearchQ('');
                }}
              >
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmRemove.open}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={() => void removeDocument()}
        onCancel={() => setConfirmRemove({ open: false, row: null })}
        confirmText={confirmBtn}
        cancelText="Cancelar"
      />
    </div>
  );
};

const HISTORY_PAGE_SIZE = 10;

const formatDateTime = (value?: string): string => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const roleLabel = (role: string): string => {
  const normalized = role?.toLowerCase();

  if (normalized === 'assistant') {
    return 'Assistente';
  }

  if (normalized === 'user') {
    return 'Usuário';
  }

  if (normalized === 'system') {
    return 'Sistema';
  }

  return role || 'Mensagem';
};

// Aba Histórico de atendimento — consulta de conversas do chatbot com paginação e cache
const AttendanceHistoryTab: React.FC<WorkspaceTabProps> = ({ workspace }) => {
  const workspaceId = workspace.id;
  const { addToast } = useToast();

  const [conversations, setConversations] = useState<WorkspaceConversationSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  const [selected, setSelected] = useState<WorkspaceConversationSummary | null>(null);
  const [messages, setMessages] = useState<WorkspaceChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!workspaceId) {
      return;
    }

    setLoading(true);

    try {
      const result = await workspaceChatHistoryService.getHistory(workspaceId, {
        page,
        pageSize: HISTORY_PAGE_SIZE,
        search: appliedSearch,
      });

      setConversations(result.data);
      setTotalCount(result.totalCount);
      setTotalPages(result.totalPages > 0 ? result.totalPages : 1);
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Erro ao carregar o histórico de atendimentos.'), 'error');
      setConversations([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, page, appliedSearch, addToast]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const applySearch = () => {
    setPage(1);
    setAppliedSearch(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput('');
    setPage(1);
    setAppliedSearch('');
  };

  const openConversation = useCallback(
    async (conversation: WorkspaceConversationSummary) => {
      setSelected(conversation);
      setMessages([]);
      setMessagesLoading(true);

      try {
        const list = await workspaceChatHistoryService.getThreadMessages(conversation.threadId);
        const ordered = [...list].sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
        setMessages(ordered);
      } catch (e: unknown) {
        addToast(apiErrorMessage(e, 'Erro ao carregar a conversa.'), 'error');
        setMessages([]);
      } finally {
        setMessagesLoading(false);
      }
    },
    [addToast]
  );

  const closeConversation = () => {
    setSelected(null);
    setMessages([]);
  };

  const userDisplay = (conversation: WorkspaceConversationSummary): string => {
    if (conversation.userName && conversation.userName.trim()) {
      return conversation.userName;
    }

    if (conversation.userEmail && conversation.userEmail.trim()) {
      return conversation.userEmail;
    }

    return 'Usuário desconhecido';
  };

  const rangeStart = totalCount === 0 ? 0 : (page - 1) * HISTORY_PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * HISTORY_PAGE_SIZE, totalCount);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Histórico de atendimento</h3>
        <p className="text-sm text-gray-600 mt-1 max-w-3xl">
          Consulte as conversas que os usuários tiveram com este chatbot. Selecione um atendimento para visualizar a
          conversa completa.
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex w-full max-w-md items-center gap-2">
          <div className="relative flex-1">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  applySearch();
                }
              }}
              placeholder="Buscar por assunto ou usuário"
              className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <Button type="button" variant="primary" onClick={applySearch} disabled={!workspaceId}>
            Buscar
          </Button>
          {appliedSearch && (
            <Button type="button" variant="secondary" onClick={clearSearch}>
              Limpar
            </Button>
          )}
        </div>
        <span className="text-sm text-gray-500">
          {totalCount === 0
            ? 'Nenhum atendimento'
            : `Exibindo ${rangeStart}-${rangeEnd} de ${totalCount} atendimento(s)`}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      ) : conversations.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 py-12 text-center">
          <p className="text-gray-500">
            {appliedSearch
              ? 'Nenhum atendimento encontrado para a busca informada.'
              : 'Este chatbot ainda não possui atendimentos registrados.'}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Usuário</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Assunto</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-700 w-32">Mensagens</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700 w-48">Último contato</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700 w-32">Ações</th>
                </tr>
              </thead>
              <tbody>
                {conversations.map((conversation) => (
                  <tr key={conversation.threadId} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 align-middle">
                      <div className="font-medium text-gray-900">{userDisplay(conversation)}</div>
                      {conversation.userName && conversation.userEmail && (
                        <div className="text-xs text-gray-500">{conversation.userEmail}</div>
                      )}
                    </td>
                    <td className="py-3 px-4 align-middle text-gray-700">{conversation.title || 'Sem assunto'}</td>
                    <td className="py-3 px-4 align-middle text-center">
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                        {conversation.messageCount}
                      </span>
                    </td>
                    <td className="py-3 px-4 align-middle text-gray-600">{formatDateTime(conversation.lastMessageAt)}</td>
                    <td className="py-3 px-4 text-right align-middle">
                      <Button
                        variant="secondary"
                        size="sm"
                        type="button"
                        onClick={() => void openConversation(conversation)}
                        title="Ver conversa"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Página {page} de {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1 || loading}
              >
                Anterior
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages || loading}
              >
                Próxima
              </Button>
            </div>
          </div>
        </>
      )}

      {selected && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-30"
          role="presentation"
          onClick={closeConversation}
        >
          <div
            className="flex h-full w-full max-w-xl flex-col bg-white shadow-xl"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-gray-200 p-5">
              <div className="min-w-0">
                <h3 className="truncate text-lg font-bold text-gray-900">{selected.title || 'Atendimento'}</h3>
                <p className="mt-0.5 text-sm text-gray-500">
                  {userDisplay(selected)} · {formatDateTime(selected.createdAt)}
                </p>
              </div>
              <button
                type="button"
                onClick={closeConversation}
                className="ml-4 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label="Fechar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto bg-gray-50 p-5">
              {messagesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
                </div>
              ) : messages.length === 0 ? (
                <p className="text-center text-gray-500">Nenhuma mensagem nesta conversa.</p>
              ) : (
                <div className="space-y-4">
                  {messages
                    .filter((message) => message.role?.toLowerCase() !== 'system')
                    .map((message) => {
                      const isAssistant = message.role?.toLowerCase() === 'assistant';
                      return (
                        <div
                          key={message.id}
                          className={`flex ${isAssistant ? 'justify-start' : 'justify-end'}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-lg px-4 py-2 shadow-sm ${
                              isAssistant
                                ? 'bg-white text-gray-800 border border-gray-200'
                                : 'bg-blue-600 text-white'
                            }`}
                          >
                            <div
                              className={`mb-1 text-xs font-medium ${
                                isAssistant ? 'text-gray-500' : 'text-blue-100'
                              }`}
                            >
                              {roleLabel(message.role)}
                            </div>
                            <p className="whitespace-pre-wrap break-words text-sm">{message.content}</p>
                            <div
                              className={`mt-1 text-right text-[11px] ${
                                isAssistant ? 'text-gray-400' : 'text-blue-100'
                              }`}
                            >
                              {formatDateTime(message.createdAt)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const WorkspaceEditPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const id = searchParams.get('id');
  const [activeTab, setActiveTab] = useState('general');
  const [isLoading, setIsLoading] = useState(false);
  const [workspace, setWorkspace] = useState<WorkspaceDto>({
    id: '',
    name: '',
    slug: '',
    assistantName: '',
    description: '',
    persona: '',
    defaultLanguage: '',
    isPublic: false,
    systemPrompt: '',
    rejectionMessage: '',
    quickMessages: [],
    avatar: '',
    isActive: true,
    aiTemperature: 0.7,
    aiHistory: 20,
    similarityThreshold: 0.25,
    chatProvider: 'openai',
    chatModel: 'gpt-4o-mini',
    visionEnabled: false,
    maxVisionImages: 3,
    maxVisionImageBytes: 5 * 1024 * 1024,
    visionDetail: 'low',
    topN: 4,
    chatMode: 'chat',
    agentProvider: '',
    agentModel: '',
    vectorSearchMode: 'similarity',
    embeddingProvider: 'openai',
    vectorDbProvider: 'postgres',
    maxChunks: 1000,
    chunkSize: 1000,
    chunkOverlap: 200,
    maxContextWindow: 8000,
    maxResponseTokens: 2000,
    accuracyPreference: 'balanced',
    accessType: 'DocumentTreeBased',
    documentScopeMode: 'bypass',
    promptLinkLabels: [],
    apiKey: '',
    tags: [],
    providerSpecificSettings: {}
  });

  const workspaceRef = useRef(workspace);
  const [persistedChatModel, setPersistedChatModel] = useState(workspace.chatModel || 'gpt-4o-mini');

  const patchWorkspace = useCallback((patch: Partial<WorkspaceDto>) => {
    const next = { ...workspaceRef.current, ...patch };
    workspaceRef.current = next;
    setWorkspace(next);
  }, []);

  const persistWorkspace = useCallback(async (): Promise<boolean> => {
    if (!id)
    {
      return false;
    }

    setIsLoading(true);
    try
    {
      const updatedWorkspace = await workspaceService.updateWorkspace(id, workspaceRef.current);
      workspaceRef.current = updatedWorkspace;
      setWorkspace(updatedWorkspace);
      setPersistedChatModel(updatedWorkspace.chatModel || 'gpt-4o-mini');
      addToast('Alterações salvas com sucesso!', 'success');
      return true;
    }
    catch (error)
    {
      console.error('Erro ao salvar:', error);
      addToast(apiErrorMessage(error, 'Erro ao salvar alterações. Tente novamente.'), 'error');
      return false;
    }
    finally
    {
      setIsLoading(false);
    }
  }, [id, addToast]);

  // Carregar workspace da API quando a página for montada
  useEffect(() => {
    const loadWorkspace = async () => {
      if (!id) return;

      setIsLoading(true);
      try {
        const workspaceData = await workspaceService.getWorkspace(id);
        workspaceRef.current = workspaceData;
        setWorkspace(workspaceData);
        setPersistedChatModel(workspaceData.chatModel || 'gpt-4o-mini');
      } catch (error) {
        console.error('Erro ao carregar workspace:', error);
        addToast('Erro ao carregar dados do chatbot. Tente novamente.', 'error');
        navigate('/chatbot-admin/workspaces');
      } finally {
        setIsLoading(false);
      }
    };

    loadWorkspace();
  }, [id]);

  const tabs = [
    {
      id: 'general',
      title: 'Geral',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    },
    {
      id: 'chat',
      title: 'Chat (LLM)',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      )
    },
    {
      id: 'embedding',
      title: 'Provedor de Embedding',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      )
    },
    {
      id: 'vector',
      title: 'Banco Vetorial',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      )
    },
    {
      id: 'documents',
      title: 'Escopo documental',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      id: 'members',
      title: 'Membros',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      )
    },
    {
      id: 'history',
      title: 'Histórico de atendimento',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return <GeneralTab workspace={workspace} onPatch={patchWorkspace} onSave={persistWorkspace} />;
      case 'chat':
        return (
          <ChatTab
            workspace={workspace}
            onPatch={patchWorkspace}
            onSave={persistWorkspace}
            persistedChatModel={persistedChatModel}
          />
        );
      case 'embedding':
        return <EmbeddingTab workspace={workspace} onPatch={patchWorkspace} onSave={persistWorkspace} />;
      case 'vector':
        return <VectorTab workspace={workspace} onPatch={patchWorkspace} onSave={persistWorkspace} />;
      case 'documents':
        return <DocumentScopeTab workspace={workspace} onPatch={patchWorkspace} onSave={persistWorkspace} />;
      case 'members':
        return <MembersTab workspace={workspace} onPatch={patchWorkspace} onSave={persistWorkspace} />;
      case 'history':
        return <AttendanceHistoryTab workspace={workspace} onPatch={patchWorkspace} onSave={persistWorkspace} />;
      default:
        return <GeneralTab workspace={workspace} onPatch={patchWorkspace} onSave={persistWorkspace} />;
    }
  };

  // Validar se o ID foi fornecido
  if (!id) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">ID do Workspace não fornecido</h2>
          <p className="text-gray-600 mb-6">Volte para a lista de chatbots e tente novamente.</p>
          <Button onClick={() => navigate('/chatbot-admin/workspaces')}>
            Voltar para Chatbots
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading && !workspace.id) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Cabeçalho */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-blue-900 mb-2">
              Editar Chatbot: {workspace.name}
            </h1>
            <p className="text-gray-600">
              Configure todos os aspectos do seu chatbot
            </p>
          </div>
          <div className="flex space-x-3">
            <Button onClick={() => navigate('/chatbot-admin/workspaces')} variant="secondary">
              Cancelar
            </Button>
            <Button onClick={() => void persistWorkspace()} disabled={isLoading}>
              {isLoading ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </div>
        </div>
      </div>

      {/* Abas */}
      <div className="mb-8">
        <div className="flex flex-wrap gap-2 border-b border-gray-200">
          {tabs.map((tab) => (
            <TabItem
              key={tab.id}
              title={tab.title}
              icon={tab.icon}
              isActive={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            />
          ))}
        </div>
      </div>

      {/* Conteúdo da Aba */}
      <div className="bg-white rounded-lg shadow p-6">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default WorkspaceEditPage;
